import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Table from '../Table/Table';
import utils from '../../../lib/utils';
import { setNavToolbarItems, clearNavToolbarItems } from '../../../actions/headerNavToolbarItemsActions';
import Autocomplete from '../SearchBar/Autocomplete';


export class PipelinePortfolioBase extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inputKeyword: '',    // used to sync typing across two searchbar instances
      searchedKeyword: '', // The most recent searched keyword, used to ensure the export is consistent with the table
    };

    // Refs:
    this.refFilter = React.createRef();
    this.refOverlaySearch = React.createRef();
    this.refSidebar = React.createRef();

    // Bindings
    this.initiateSearch = this.initiateSearch.bind(this);
    this.getQuery = this.getQuery.bind(this);
    this.sort = this.sort.bind(this);
    this.export = this.export.bind(this);
    this.openSidebar = this.openSidebar.bind(this);
    this.closeSidebar = this.closeSidebar.bind(this);
    this.toggleOverlaySearch = this.toggleOverlaySearch.bind(this);
    this.changePage = this.changePage.bind(this);
  }

  componentDidMount() {
    // add search and sidebar toggles to Header toolbar area (toolbar area only shows on Mobile screens)
    this.props.setNavToolbarItems([
      <button key="toggle-search" className="button" onClick={this.toggleOverlaySearch}><i className="icon icon-magnifier" /></button>,
      <button key="toggle-sidebar" className="button" onClick={this.openSidebar}><i className="icon icon-equalizer" /></button>
    ]);

    // fetch initial data
    this.props.fetchFilter(this.getQuery());
  }

  componentWillUnmount() {
    // remove search and sidebar toggles from Header toolbar area (toolbar area only shows on Mobile screens)
    this.props.clearNavToolbarItems();
  }

  openSidebar() {
    this.refSidebar.current.classList.add('active');
    utils.disablePageScroll();
  }

  closeSidebar() {
    this.refSidebar.current.classList.remove('active');
    utils.resumePageScroll();
  }

  // slidedown searchbox from the top
  toggleOverlaySearch() {
    const overlaySearch = this.refOverlaySearch.current;
    if (overlaySearch.classList.contains('active')) {
      overlaySearch.classList.remove('active');
      utils.resumePageScroll();
    } else {
      overlaySearch.classList.add('active');
      utils.disablePageScroll();
      // set search input to focus
      const input = this.refOverlaySearch.current.querySelector('input');
      input.focus(); // set to focus
    }
  }

  getQuery({ isExport = false, page = 1, sort = null, order = null } = {}) {
    const filters = this.refFilter.current.getAppliedFilters();
    const keyword = isExport ? this.state.searchedKeyword : this.state.inputKeyword;

    const query = {
      ...filters,
      q: keyword,
      page, sort, order,
      export: isExport,
      ...this.props.selectedPartner,
    };

    return query;
  }

  // initiate search with keyword and filter configs
  initiateSearch() {
    const query = this.getQuery();
    this.props.fetchFilter(query);
    this.setState({ searchedKeyword: this.state.inputKeyword });

    // on Mobile: close filters panel sidebar if the panel is open
    if (this.refSidebar.current.classList.contains('active')) {
      this.closeSidebar();
    }

    // istanbul ignore next
    if (this.props.delegate && this.props.delegate.didInitiateSearch){
      this.props.delegate.didInitiateSearch(query);
    }
  }

  export() {
    const query = this.getQuery({ isExport: true });
    this.props.exportFilter(query);

    // istanbul ignore next
    if (this.props.delegate && this.props.delegate.didExport) {
      this.props.delegate.didExport(query);
    }
  }

  changePage(page) {
    const query = this.getQuery({ page });
    this.props.fetchFilter(query);

    // istanbul ignore next
    if (this.props.delegate && this.props.delegate.didChangePage) {
      this.props.delegate.didChangePage(query);
    }
  }

  sort(column, order) {
    const query = this.getQuery({sort: column, order});
    this.props.fetchFilter(query);

    // istanbul ignore next
    if (this.props.delegate && this.props.delegate.didSort){
      this.props.delegate.didSort(query);
    }
  }

  updatekeyWord (newKeyword) {
    this.setState({inputKeyword: newKeyword});
  }


  render() {
    const Filter = this.props.FilterComponent;
    const {
      headers, values, data, totalPages, currentPage, dataLabels,
      isSearchError, actionCellRenderer, isAdmin, hasSpinner
    } = this.props.tableInfo;

    return (
      <React.Fragment>
        <div className="overlay-search" ref={this.refOverlaySearch}>
          <div className="overlay" onClick={this.toggleOverlaySearch} />
          <div className="controls">
            <Autocomplete search={() => { this.toggleOverlaySearch(); this.initiateSearch(); }} onChange={this.updatekeyWord.bind(this)} />
            <a onClick={this.toggleOverlaySearch}>Close</a>
          </div>
        </div>
        <div className="pipeline-portfolio-page column page">
          <div className="header">{this.props.headerText}</div>
          <div className="content">
            <div className="sidebar" ref={this.refSidebar}>
              <Filter applyFilters={this.initiateSearch} ref={this.refFilter} />
            </div>
            <div className="main">
              <div className="top-toolbar hidden-sm-down">
                <Autocomplete search={this.initiateSearch} onChange={this.updatekeyWord.bind(this)} />
                <button className="button is-primary button-export" onClick={this.export}><i className="icon icon-export" />Export</button>
              </div>
              <button className="button is-primary button-export hidden-md-up" onClick={this.export}>
                <i className="icon icon-export" />Export
              </button>
              <Table
                headers={headers}
                values={values}
                tableData={data}
                totalPages={totalPages}
                currentPage={currentPage}
                dataLabels={dataLabels}
                changePage={(to) => this.changePage(to)}
                sortQuery={(column, order) => this.sort(column, order)}
                searchError={isSearchError}
                renderActionCell={actionCellRenderer}
                isAdmin={isAdmin}
                hasSpinner = {hasSpinner}
                useTablePaginator
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

PipelinePortfolioBase.propTypes = {
  headerText: PropTypes.string.isRequired,
  fetchFilter: PropTypes.func.isRequired,
  exportFilter: PropTypes.func.isRequired,
  delegate: PropTypes.object, // action delegate
  FilterComponent: PropTypes.elementType.isRequired,
  tableInfo: PropTypes.shape({
    headers: PropTypes.array,
    values: PropTypes.array,
    data: PropTypes.array,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number,
    dataLabels: PropTypes.array,
    isSearchError: PropTypes.bool,
    actionCellRenderer: PropTypes.func,
    isAdmin: PropTypes.bool,
    hasSpinner: PropTypes.bool
  }).isRequired,
  selectedPartner: PropTypes.object,

  // redux props
  setNavToolbarItems: PropTypes.func,
  clearNavToolbarItems: PropTypes.func,
};

// istanbul ignore next
function mapDispatchToProps(dispatch) {
  return {
    setNavToolbarItems: bindActionCreators(setNavToolbarItems, dispatch),
    clearNavToolbarItems: bindActionCreators(clearNavToolbarItems, dispatch),
  };
}

export default connect(null, mapDispatchToProps)(PipelinePortfolioBase);
